import React, { useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';

import Layout from '../Layout';
import { Header } from '../../components';
import { MenuMain, MenuFooter } from '../../implementations';
import useProgrammaticFocus from '../../hooks/useProgrammaticFocus';

import './style.scss';

const Page = ({
  children,
  className,
  loading,
  referenceLoading,
  title,
  hideMenuMain,
  hideChrome,
  hideCrisisBanner,
  hideHeader,
  showFooter,
  /** The mechanism that ensures pages automatically scroll to top when navigated to */
  scrollToTop = true,
  ...props
}) => {
  const hasLoadingItems = referenceLoading.get('itemsStarted') > 0;
  const isLoading = hasLoadingItems && referenceLoading.get('itemsStarted') !== referenceLoading.get('itemsFinished');

  const siteNameRaw = useSelector((state) => state.getIn(['uiInternal', 'currentDomain', 'raw'], null));
  const siteNamePretty = useSelector((state) => state.getIn(['uiInternal', 'currentDomain', 'pretty'], null));

  const focusToBody = useProgrammaticFocus(document.body);

  useEffect(() => {
    if (scrollToTop) {
      window.scrollTo(0, 0);

      focusToBody();
    }
  }, [title, scrollToTop]);

  const classes = classNames(className, 'Page');
  const contentWrapperClasses = classNames('Page__inner-wrapper', `${className}__inner-wrapper`, {
    'has-loading-items': hasLoadingItems,
    loading: isLoading,
  });

  const documentTitle = useMemo(() => {
    // If both title and site name have not been provided, fall back to just Zelfhulp.
    if (!title && !siteNamePretty) {
      return 'Zelfhulp';
    }

    // If only a title has been provided, use that.
    if (title && !siteNamePretty) {
      return title;
    }

    // If only a site name has been provided, use that.
    if (!title && siteNamePretty) {
      return siteNamePretty;
    }

    // If both have been provided, create the requested title.
    return `${title} | ${siteNamePretty}`;
  }, [title, siteNamePretty]);

  return (
    <div className={classes}>
      <Helmet>
        <title>{documentTitle}</title>
      </Helmet>
      {!hideHeader && (
        <Header
          siteName={siteNameRaw}
          content={() => {
            if (!hideMenuMain) {
              return <MenuMain />;
            }
          }}
        />
      )}
      <Layout
        className={contentWrapperClasses}
        hideChrome={hideChrome}
      >
        {children}
      </Layout>

      {showFooter && (
        <MenuFooter />
      )}
    </div>
  );
};

function mapStateToProps(state) {
  return {
    referenceLoading: state.getIn(['ui', 'referenceLoading']),
  };
}

export default connect(mapStateToProps)(Page);
